export const formatDate = (date: Date) =>
  date?.toLocaleDateString('en-US', { timeZone: 'UTC', month: '2-digit', day: '2-digit', year: 'numeric' });

export const addWorkDays = (startDate: Date, days: number) => {
  // Get the day of the week as a number (0 = Sunday, 1 = Monday, .... 6 = Saturday)
  const dow = startDate.getDay();
  let daysToAdd = days;
  // If the current day is Sunday add one day
  if (dow === 0) daysToAdd += 1;
  // If the start date plus the additional days falls on or after the closest Saturday calculate weekends
  if (dow + daysToAdd >= 6) {
    // Subtract days in current working week from work days
    const remainingWorkDays = daysToAdd - (5 - dow);
    // Add current working week's weekend
    daysToAdd += 2;
    if (remainingWorkDays > 5) {
      // Add two days for each working week by calculating how many weeks are included
      daysToAdd += 2 * Math.floor(remainingWorkDays / 5);
      // Exclude final weekend if remainingWorkDays resolves to an exact number of weeks
      if (remainingWorkDays % 5 === 0) daysToAdd -= 2;
    }
  }
  startDate.setDate(startDate.getDate() + daysToAdd);
  return startDate;
};

// Get n years from today's date
export const getYearsFromNow = (years: number): Date =>
  new Date(new Date().setFullYear(new Date().getFullYear() + years));

const MONTHS_CONVERSION_LIMIT = 36;

enum TimeFrame {
  Years = 'years',
  Months = 'months',
}

export const getRemainingTerm = (months: number): { amount: number; timeFrame: TimeFrame } => {
  if (months > MONTHS_CONVERSION_LIMIT) {
    return {
      amount: Math.floor(months / 12),
      timeFrame: TimeFrame.Years,
    };
  }

  return {
    amount: months,
    timeFrame: TimeFrame.Months,
  };
};

export const formatTextDate = (date: Date, options?: Intl.DateTimeFormatOptions) =>
  date?.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric', ...options });

export const formatTextDateAndTime = (date: Date, options?: Intl.DateTimeFormatOptions) =>
  date?.toLocaleDateString('en-US', {
    month: 'long',
    day: '2-digit',
    hour: 'numeric',
    minute: date.getMinutes() ? 'numeric' : undefined,
    ...options,
  });

export const formatDateTimeWithToday = (date: Date): string => {
  const today = new Date();
  const isDateToday = isSameDay(date.toISOString(), today.toISOString());

  const timeString = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${isDateToday ? 'Today' : formatTextDate(date, { month: 'short' })} at ${timeString}`;
};

export const formatMonthYear = (date: Date) => date?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

export const formatMonthDay = (date: Date, options?: Intl.DateTimeFormatOptions) =>
  date?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', ...options });

export const formatMonthName = (date: Date, options?: Intl.DateTimeFormatOptions) =>
  date?.toLocaleDateString('en-US', { month: 'long', ...options });

export const formatShortDate = (date: Date) =>
  date?.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });

export const addDays = (date: Date, days: number) => {
  date.setDate(date.getDate() + days);
  return date;
};

export const addWeeks = (date: Date, weeks: number) => {
  date.setDate(date.getDate() + weeks * 7);
  return date;
};

export const addMonths = (date: Date, months: number) => {
  date.setMonth(date.getMonth() + months);
  return date;
};

export const isBusinessHours = (date: Date, utcHourStart: number, utcHourEnd: number): boolean => {
  const hour = date.getHours() + date.getTimezoneOffset() / 60;
  return date.getDay() >= 2 && hour >= utcHourStart && hour < utcHourEnd;
};

// Support https://caniuse.com/mdn-javascript_builtins_intl_datetimeformat_resolvedoptions_computed_timezone
export const getClientTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const formatToYearMonth = (date: Date) => date.toISOString().slice(0, 7);

export const formatYearMonthToDate = (date: string) => {
  const [year, month] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, 1));
};

export const isValidDateString = (dateString: string): boolean | Date => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) return false;

  const [year, month, day] = dateString.split('-').map(Number);

  if (year < 1900 || year > 3000) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  return true;
};

export const formatYearMonthDayToDate = (date?: string | null) => {
  if (!date) return null;
  if (!isValidDateString(date)) return null;

  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const formatDateToInput = (date: Date | null): string => {
  if (!date) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const isSameDay = (date1: string, date2: string): boolean => {
  if (!date1 || !date2) return false;
  const d1 = formatDateToInput(new Date(date1));
  const d2 = formatDateToInput(new Date(date2));
  return d1 === d2;
};

// create an isSameMonth function that accepts either a date string or a date object
export const isSameMonth = (date1: string | Date, date2: string | Date): boolean => {
  if (!date1 || !date2) return false;
  const d1 = formatToYearMonth(new Date(date1));
  const d2 = formatToYearMonth(new Date(date2));

  return d1 === d2;
};

export const startOfDay = (date?: Date): Date => {
  const newDate = date ? new Date(date) : new Date();
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};
